import React, { useEffect, useState } from "react";
import {
  Layout,
  Card,
  Form,
  Button,
  Row,
  Col,
  Spin,
  DatePicker,
  Select,
  InputNumber,
  Input,
  Descriptions,
} from "antd";
import { RollbackOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";

import LayoutHeader from "../inc/LayoutHeader";
import LayoutFooter from "../inc/LayoutFooter";

import { getAPI, postAPI, displayMessage } from "../../utils/common";
import {
  KITCHEN_PURCHASE_LIST,
  KITCHEN_PURCHASE_DETAIL,
  KITCHEN_PURCHASE_RETURN_CREATE,
} from "../../constants/api";
import { ERROR_MSG_TYPE, SUCCESS_MSG_TYPE } from "../../constants/messages";

function KitchenPurchaseReturnAdd() {
  const { Content } = Layout;
  const navigate = useNavigate();

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);

  const [purchases, setPurchases] = useState([]);
  const [selectedPurchase, setSelectedPurchase] = useState(null);

  const [totalAmount, setTotalAmount] = useState(0);

  useEffect(() => {
    fetchPurchases();
  }, []);

  const fetchPurchases = () => {
    getAPI(KITCHEN_PURCHASE_LIST, (res) => {
      if (res.status) setPurchases(res.data);
    });
  };

  const handleValuesChange = (_, allValues) => {
    const items = allValues.items || [];

    let total = 0;

    items.forEach((item) => {
      const qty = parseFloat(item.return_qty || 0);
      const rate = parseFloat(item.rate || 0);

      total += qty * rate;
    });

    setTotalAmount(total);
  };

  const handlePurchaseSelect = (id) => {
    setLoading(true);

    postAPI(
      KITCHEN_PURCHASE_DETAIL,
      { id },
      (res) => {
        if (res.status) {
          const data = res.data;
          setSelectedPurchase(data);

          // Populate items
          form.setFieldsValue({
            items: data.items.map((item) => {
              const returned = parseFloat(item.returned_qty || 0);
              const purchased = parseFloat(item.qty);

              return {
                item_id: item.item_id,
                item_name: item.item?.name,
                item_code: item.item?.item_code,
                purchased_qty: purchased,
                returned_qty: returned,
                remaining_qty: purchased - returned,
                return_qty: 0,
                rate: parseFloat(item.rate),
                reason: "",
              };
            }),
          });
        }
        setLoading(false);
      },
      () => setLoading(false),
    );
  };
  const submitReturn = (values) => {
    setLoading(true);

    const filteredItems = values.items.filter((i) => Number(i.return_qty) > 0);

    console.log("filteredItems: ", filteredItems.length);

    // VALIDATION: no items selected
    if (filteredItems.length === 0) {
      setLoading(false);
      displayMessage(
        ERROR_MSG_TYPE,
        "Please enter at least one return quantity",
      );
      return;
    }

    const payload = {
      purchase_id: selectedPurchase.id,
      return_date: dayjs(values.return_date).format("YYYY-MM-DD HH:mm:ss"),
      return_by: values.return_by,
      remark: values.remark,
      items: filteredItems.map((row) => ({
        item_id: row.item_id,
        purchased_qty: row.purchased_qty,
        return_qty: row.return_qty,
        rate: row.rate,
        reason: row.reason,
      })),
    };

    postAPI(
      KITCHEN_PURCHASE_RETURN_CREATE,
      payload,
      (res) => {
        if (res.status) {
          displayMessage(SUCCESS_MSG_TYPE, res.message);
          navigate("/kitchen/purchase-return");
        } else {
          displayMessage(ERROR_MSG_TYPE, res.message);
        }
        setLoading(false);
      },
      () => {
        setLoading(false);
        displayMessage(ERROR_MSG_TYPE, "Failed to create return");
      },
    );
  };

  return (
    <Layout>
      <LayoutHeader />
      <Content>
        <Card
          title="Store Purchase Return"
          extra={
            <Button onClick={() => navigate(-1)}>
              <RollbackOutlined />
            </Button>
          }
        >
          <Spin spinning={loading}>
            {/* 🔹 Step 1: Select Purchase */}
            <Row gutter={16}>
              <Col span={12}>
                <Select
                  placeholder="Select Purchase"
                  style={{ width: "100%" }}
                  onChange={handlePurchaseSelect}
                >
                  {purchases.map((p) => (
                    <Select.Option key={p.id} value={p.id}>
                      #{p.id} - {p.supplier_name}
                    </Select.Option>
                  ))}
                </Select>
              </Col>
              <Col span={12}>
                <h3 style={{ textAlign: "center", fontWeight: "bolder" }}>
                  Total Return Value: ₹ {totalAmount.toFixed(2)}
                </h3>
              </Col>
            </Row>

            {/* 🔹 Step 2: Show Purchase Info */}
            {selectedPurchase && (
              <>
                <Card size="small" style={{ marginTop: 20 }}>
                  <Descriptions column={2} bordered size="small">
                    <Descriptions.Item label="Purchase ID">
                      #{selectedPurchase.id}
                    </Descriptions.Item>

                    <Descriptions.Item label="Purchase Date">
                      {dayjs(selectedPurchase.purchase_date).format(
                        "DD MMM YYYY",
                      )}
                    </Descriptions.Item>

                    <Descriptions.Item label="Supplier">
                      {selectedPurchase.supplier_name}
                    </Descriptions.Item>
                  </Descriptions>
                </Card>

                {/* 🔹 Step 3: Return Form */}
                <Form
                  form={form}
                  layout="vertical"
                  onFinish={submitReturn}
                  style={{ marginTop: 20 }}
                  onValuesChange={handleValuesChange}
                >
                  <Row gutter={16}>
                    <Col span={6}>
                      <Form.Item
                        name="return_date"
                        label="Return Date"
                        rules={[{ required: true }]}
                        initialValue={dayjs()}
                      >
                        <DatePicker style={{ width: "100%" }} />
                      </Form.Item>
                    </Col>

                    <Col span={6}>
                      <Form.Item
                        name="return_by"
                        label="Return By"
                        rules={[{ required: true }]}
                      >
                        <Input />
                      </Form.Item>
                    </Col>

                    <Col span={12}>
                      <Form.Item name="remark" label="Remark">
                        <Input />
                      </Form.Item>
                    </Col>
                  </Row>

                  {/* 🔹 Items */}
                  <Card title="Return Items" size="small">
                    <Form.List name="items">
                      {(fields) => (
                        <>
                          {fields.map(({ key, name }) => (
                            <Row gutter={16} key={key}>
                              <Col span={3}>
                                <Form.Item
                                  name={[name, "item_code"]}
                                  label="Code"
                                >
                                  <Input disabled />
                                </Form.Item>
                              </Col>

                              <Col span={5}>
                                <Form.Item
                                  name={[name, "item_name"]}
                                  label="Item"
                                >
                                  <Input disabled />
                                </Form.Item>
                              </Col>

                              <Form.Item name={[name, "item_id"]} hidden>
                                <Input />
                              </Form.Item>

                              {/* Purchased */}
                              <Col span={2}>
                                <Form.Item
                                  name={[name, "purchased_qty"]}
                                  label="Purchased"
                                >
                                  <InputNumber
                                    disabled
                                    style={{ width: "100%" }}
                                  />
                                </Form.Item>
                              </Col>

                              {/* Already Returned */}
                              <Col span={2}>
                                <Form.Item
                                  name={[name, "returned_qty"]}
                                  label="Returned"
                                >
                                  <InputNumber
                                    disabled
                                    style={{ width: "100%" }}
                                  />
                                </Form.Item>
                              </Col>

                              {/* Remaining */}
                              <Col span={2}>
                                <Form.Item
                                  name={[name, "remaining_qty"]}
                                  label="Remaining"
                                >
                                  <InputNumber
                                    disabled
                                    style={{ width: "100%" }}
                                  />
                                </Form.Item>
                              </Col>

                              {/* Return Qty */}
                              <Col span={2}>
                                <Form.Item
                                  name={[name, "return_qty"]}
                                  label="Return Qty"
                                  rules={[
                                    { required: true },
                                    ({ getFieldValue }) => ({
                                      validator(_, value) {
                                        const remaining = getFieldValue([
                                          "items",
                                          name,
                                          "remaining_qty",
                                        ]);

                                        if (value <= remaining)
                                          return Promise.resolve();

                                        return Promise.reject(
                                          "Exceeds remaining qty",
                                        );
                                      },
                                    }),
                                  ]}
                                >
                                  <InputNumber
                                    min={0}
                                    style={{ width: "100%" }}
                                    disabled={
                                      form.getFieldValue([
                                        "items",
                                        name,
                                        "remaining_qty",
                                      ]) <= 0
                                    }
                                  />
                                </Form.Item>
                              </Col>

                              {/* Rate */}
                              <Col span={2}>
                                <Form.Item name={[name, "rate"]} label="Rate">
                                  <InputNumber
                                    disabled
                                    style={{ width: "100%" }}
                                  />
                                </Form.Item>
                              </Col>

                              {/* Reason */}
                              <Col span={6}>
                                <Form.Item
                                  name={[name, "reason"]}
                                  label="Reason"
                                >
                                  <Input />
                                </Form.Item>
                              </Col>
                            </Row>
                          ))}
                        </>
                      )}
                    </Form.List>
                  </Card>
                  <Row>
                    <Col span={24}>
                      <Form.Item style={{ float: "right", marginTop: 16 }}>
                        <Button
                          type="primary"
                          htmlType="submit"
                          loading={loading}
                        >
                          Submit Return
                        </Button>
                      </Form.Item>
                    </Col>
                  </Row>
                </Form>
              </>
            )}
          </Spin>
        </Card>
      </Content>
      <LayoutFooter />
    </Layout>
  );
}

export default KitchenPurchaseReturnAdd;
